import { ref, readonly } from 'vue'
import { FeatureFlagsService, type FlagEnum } from '@/types/generated'

const flagCache = new Map<FlagEnum, { value: boolean; timestamp: number }>()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Composable for checking feature flags
 *
 * This is the first implementation of client-side feature flags in JUPUS.
 * Provides a reactive interface to the PostHog feature flag system with caching.
 *
 * Usage:
 * ```vue
 * <script setup>
 * const officeEditingFlag = useFeatureFlag('OFFICE_DOCUMENT_EDITING')
 *
 * onMounted(async () => {
 *   await officeEditingFlag.checkFlag()
 * })
 *
 * const showEditButton = computed(() =>
 *   officeEditingFlag.isEnabled.value && isOfficeFile(document)
 * )
 * </script>
 * ```
 */
export function useFeatureFlag(flag: FlagEnum) {
  const isEnabled = ref<boolean>(false)
  const isLoading = ref<boolean>(false)

  /**
   * Check if the feature flag is enabled
   *
   * Includes caching to avoid repeated API calls and graceful error handling.
   * Safe default: returns false on any error (network, auth, etc.)
   */
  const checkFlag = async (): Promise<boolean> => {
    if (isLoading.value) return isEnabled.value

    const cached = flagCache.get(flag)
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      isEnabled.value = cached.value
      return cached.value
    }

    try {
      isLoading.value = true
      const response = await FeatureFlagsService.featureFlagsCheckCreate({
        requestBody: { flag }
      })

      const enabled = response.enabled
      isEnabled.value = enabled

      // Cache the result
      flagCache.set(flag, { value: enabled, timestamp: Date.now() })

      return enabled
    } catch (error) {
      console.warn(`Feature flag check failed for ${flag}:`, error)
      // Safe default: disable feature on any error (network, auth, PostHog down, etc.)
      isEnabled.value = false
      flagCache.set(flag, { value: false, timestamp: Date.now() })
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear the cache for this flag
   *
   * Useful for testing or when you need to force a fresh check
   */
  const clearCache = () => {
    flagCache.delete(flag)
  }

  return {
    isEnabled: readonly(isEnabled),
    isLoading: readonly(isLoading),
    checkFlag,
    clearCache
  }
}
